import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { WidgetManifestEntry, WidgetParameter } from '@/models/widget';
import { SharedDesign } from '@/pages/widgetEditor/types';
import { WidgetDesignFields } from '@/pages/widgetEditor/components/WidgetDesignFields';

interface WidgetDesignCardProps {
    widgets: WidgetManifestEntry[];
    design: SharedDesign;
    onChange: (name: string, value: string) => void;
}

/**
 * The parameter spec to render a shared design key with, taken from the first
 * widget that declares it. The knobs are declared centrally in the portal, so
 * every widget describes a given key identically; first-wins is a tiebreak, not
 * an invariant.
 */
const findParamSpec = (
    widgets: WidgetManifestEntry[],
    name: string
): WidgetParameter | undefined => {
    for (const widget of widgets) {
        const param = widget.parameters[name];
        if (param?.adjustable) return param;
    }
    return undefined;
};

/**
 * The design of the whole embed, configured once. Every added widget follows
 * these values unless it switches on its own override.
 *
 * It renders before any widget has been added — it is the page's design, not a
 * widget's — but stays hidden until the manifest has told us what the knobs are.
 */
export const WidgetDesignCard: FunctionComponent<WidgetDesignCardProps> = ({
    widgets,
    design,
    onChange,
}) => {
    const names = Object.keys(design);

    if (widgets.length === 0 || names.length === 0) return null;

    return (
        <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='h6'>Design</Typography>
            </CardContent>
            <Divider sx={{ width: '100%' }} />

            <Box sx={{ px: 6, pt: 3 }}>
                <Typography color='text.secondary' variant='body2'>
                    Gilt für alle hinzugefügten Widgets. Einzelne Widgets können
                    davon abweichen.
                </Typography>
            </Box>

            <WidgetDesignFields
                names={names}
                getParam={(name) => findParamSpec(widgets, name)}
                getValue={(name) =>
                    design[name] ?? findParamSpec(widgets, name)?.default ?? ''
                }
                onChange={onChange}
            />
        </Card>
    );
};
